/**
 * Screenshot intake from the bot (specs/c2-kontratlar.md 2.4.6, K11). The body is the
 * bot's CreateTaskInput unchanged (apps/telegram/src/api/types.ts). The file lies in
 * the intake volume under its storage key; the API encrypts it into
 * attachments/pii/<asset_id>.enc and deletes the temporary file.
 */
import { z } from 'zod';
import { boundedText, idStringSchema, sha256Schema, telegramUserIdSchema } from '../ids.js';
/** The bot stores screenshots up to 20 MB. */
export const INTAKE_MAX_BYTES = 20 * 1024 * 1024;
export const INTAKE_MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/** File extension the bot writes for each media type (apps/telegram/src/storage.ts). */
export const INTAKE_EXTENSIONS = Object.freeze({
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
});
/** telegram/<first 2 of sha>/<sha>.<ext>; anything else is 422 attachment_mismatch. */
export const STORAGE_KEY_PATTERN = /^telegram\/([0-9a-f]{2})\/([0-9a-f]{64})\.(png|jpg|webp)$/;
export const storedAttachmentRefSchema = z.strictObject({
    storageKey: z.string().min(1).max(200),
    sha256: sha256Schema,
    sizeBytes: z.number().int().positive().max(INTAKE_MAX_BYTES),
    mediaType: z.enum(INTAKE_MEDIA_TYPES),
});
/**
 * True when the storage key is the bot's format and agrees with the sha256 and the
 * media type. A false here is 422 attachment_mismatch, not a schema error.
 */
export function attachmentRefMatches(ref) {
    const match = STORAGE_KEY_PATTERN.exec(ref.storageKey);
    if (!match)
        return false;
    return (match[1] === ref.sha256.slice(0, 2) && match[2] === ref.sha256 && match[3] === INTAKE_EXTENSIONS[ref.mediaType]);
}
/** POST /api/v1/tasks (Idempotency-Key required). */
export const createTaskRequestSchema = z.strictObject({
    kind: z.literal('screenshot_intake'),
    source: z.literal('telegram'),
    attachment: storedAttachmentRefSchema,
    /** The bot cuts a caption to 1024 code points. */
    caption: boundedText(0, 1024).nullable(),
    telegram: z.strictObject({
        userId: telegramUserIdSchema,
        chatId: z.number().int(),
        messageId: z.number().int().positive(),
        mediaGroupId: z.string().min(1).max(64).nullable(),
    }),
});
export const createTaskResponseSchema = z.object({
    /** The only field the bot reads. */
    taskId: idStringSchema,
    /** False when the same Idempotency-Key came again (the existing task is returned). */
    created: z.boolean(),
    assetId: z.uuid(),
});
/** Screenshot assets and attachments are kept at most 90 days (buyer.attachments_max_retention_chk). */
export const SCREENSHOT_RETENTION_DAYS = 90;
//# sourceMappingURL=tasks.js.map